import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useContacts, useUpdateContact } from "@/hooks/use-contacts";
import { Users, UserPlus, User } from "lucide-react";
import { toast } from "sonner";

interface AssignAdminModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  onSuccess?: () => void;
}

export function AssignAdminModal({ 
  open, 
  onOpenChange, 
  organizationId, 
  organizationName,
  onSuccess 
}: AssignAdminModalProps) {
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const { data: allContacts } = useContacts();
  const updateContact = useUpdateContact();

  // Filter contacts that are already assigned to this organization
  const availableContacts = allContacts?.filter(contact => 
    contact.organization_id === organizationId
  ) || [];

  const handleAssignAdmin = async () => {
    if (!selectedContactId) {
      toast.error("Please select a contact to assign as admin");
      return;
    }

    const selectedContact = allContacts?.find(contact => contact.id === selectedContactId);
    if (!selectedContact) {
      toast.error("Selected contact not found");
      return;
    }

    try {
      await updateContact.mutateAsync({
        id: selectedContactId,
        data: {
          organization_id: organizationId,
          job_title: selectedContact.job_title || "Administrator",
          relationship_status: "Customer"
        }
      });

      toast.success(`${selectedContact.first_name} ${selectedContact.last_name} has been assigned as an administrator for ${organizationName}`);
      onSuccess?.();
      onOpenChange(false);
      setSelectedContactId("");
    } catch (error) {
      console.error("Error assigning admin:", error);
      toast.error("Failed to assign administrator");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Assign Company Administrator
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Organization</Label>
            <div className="p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{organizationName}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-select">Select Contact</Label>
            {availableContacts.length === 0 ? (
              <div className="p-4 text-center text-slate-500 bg-slate-50 rounded-lg border-2 border-dashed">
                <Users className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm">No contacts found for this company</p>
                <p className="text-xs text-slate-400 mt-1">
                  Add contacts to this organization first
                </p>
              </div>
            ) : (
              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                <SelectTrigger id="contact-select">
                  <SelectValue placeholder="Choose a contact to assign as admin" />
                </SelectTrigger>
                <SelectContent>
                  {availableContacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <div>
                          <div className="font-medium">
                            {contact.first_name} {contact.last_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {contact.email} {contact.job_title && `• ${contact.job_title}`}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignAdmin}
            disabled={!selectedContactId || updateContact.isPending}
            className="min-w-24 bg-[hsl(60,73%,76%)] text-black hover:bg-[hsl(60,73%,70%)]"
          >
            {updateContact.isPending ? "Assigning..." : "Assign Admin"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}